import React, { useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { TableCustom, WrapperAdvancedSearch } from '../../components/common'
import { useManagedContext, useNav } from '../../components/hooks'
import { useUsuarios } from '../../components/hooks/useUsuarios';
import { useAccess } from '../../components/hooks/useAccess';
import { ServerRequestAsync } from '../../utils/apiweb';
import { ALERT_TYPE, REQUEST_METHOD, ShowToastMessage } from '@laramie/ui';
import { APIS } from '../../config/apis';
import { onRequestError, onSearchNoSuccess } from '../../utils/requests';
import { VALIDATION_MESSAGE } from "../../consts/validationMessage"
import { LISTAS } from '../../consts/listas';
import { getDateEndOfDaySerialize, getDateToString } from '../../utils/convert';
import { APPCONFIG } from '../../app.config';

const USUARIO_CACHE_KEY = 'Auditoria_UsuarioCache'

function HomeView() {
    const navigate = useNav()
    const { setIsLoading } = useManagedContext()

    const [items, setItems] = useState([])
    const [tableVisible, setTableVisible] = useState(false)
    const [searchMessage, setSearchMessage] = useState(null)

    const [getNombreUsuario, resolveUsuarios, resolveAllUsuarios] = useUsuarios({ key: USUARIO_CACHE_KEY })
    const usuarioCache = useSelector((state) => state.memo.cache[USUARIO_CACHE_KEY]?.value)

    const usuarioOptions = useMemo(() => {
        return Object.entries(usuarioCache ?? {})
            .filter(([, nombre]) => nombre)
            .map(([id, nombre]) => ({ id: Number(id), nombre }))
            .sort((a, b) => a.nombre.localeCompare(b.nombre))
    }, [usuarioCache])

    const [hasViewAccess, setHasViewAccess] = useState(false)
    useAccess({
        key: '_SecurityAccesses',
        onLoaded: (data, isSuccess) => {
            if (isSuccess) setHasViewAccess(data.some(x => x.codigo === 'auditoria_view'))
        }
    })

    const searchRef = useRef()

    const Search = () => {
        const { filters } = searchRef.current

        setIsLoading(true)
        setSearchMessage(null)

        const tipos = [
            { nombre: 'Evento', url: APIS.URLS.EVENTO, },
            { nombre: 'Alerta', url: APIS.URLS.ALERTA,  },
            { nombre: 'Incidencia', url: APIS.URLS.INCIDENCIA, },
        ].filter(tipo => filters.tipo == 0 || tipo.nombre === filters.tipo)

        const params = `/filter` +
            `?idModulo=${filters.idModulo ?? ''}` +
            `&idUsuario=${filters.idUsuario ?? ''}` +
            `&fechaDesde=${filters.fechaDesde ?? ''}` +
            `&fechaHasta=${encodeURIComponent(getDateEndOfDaySerialize(filters.fechaHasta))}` +
            `&id=${filters.id ?? ''}` +
            `&mensaje=${filters.mensaje ?? ''}` +
            `&contenido=${filters.contenido ?? ''}`

        const promises = tipos.map(async tipo => {
            const results = await (await ServerRequestAsync(REQUEST_METHOD.GET, null, true, tipo.url, params)).json()
            return results.map(result => ({ ...result, tipo: tipo.nombre }))
        })

        return Promise.all(promises).then(async results => {
            // Las colecciones llegan por separado: se intercalan del más reciente al más antiguo.
            const merged = results.reduce((prev, curr) => [...prev, ...curr], [])
                .sort((a, b) => new Date(b.fecha) - new Date(a.fecha) || b.id - a.id)
            setItems(merged)
            resolveUsuarios(merged.map(e => e.idUsuario))
            setTableVisible(true)
            return true
        }).catch(err => {
            if (err instanceof Response) {
                onSearchNoSuccess(err, () => {
                    setItems([])
                    setTableVisible(false)
                    setSearchMessage(VALIDATION_MESSAGE.TOO_MANY_RESULTS)
                })
                return false
            }
            onRequestError(err)
            return false
        }).finally(() => {
            setIsLoading(false)
        })
    }

    const avmr = {
        onView: (item) => {
            const endpoints = {
                'Evento': 'evento',
                'Alerta': 'alerta',
                'Incidencia': 'incidencia',
            }

            const url = `${APPCONFIG.SITE.WEBAPP}${endpoints[item.tipo]}/${item.id}`
            window.open(url)
        }
    }

    function ValidFilters(aplicados) {
        const contenidoAplicado = aplicados.some(x => x.field === 'contenido')
        const otrosFiltrosAplicados = aplicados.filter(x => x.field !== 'contenido').length
        if (contenidoAplicado && otrosFiltrosAplicados < 2) {
            ShowToastMessage(ALERT_TYPE.ALERT_WARNING, 'Para buscar por contenido, aplicá al menos dos filtros.')
            return false
        }
        return true
    }

    if (!hasViewAccess) return <></>

    return <>

        <section className='section-accordion'>
            <WrapperAdvancedSearch
                message={searchMessage}
                ref={searchRef}
                onValidation={ValidFilters}
                filters={[
                    {
                        title: 'Tipo de colección',
                        field: 'tipo',
                        type: 'selectLista',
                        size: 3,
                        options: [
                            { value: 0, label: 'Todos', },
                            { value: 'Evento', label: 'Evento', },
                            { value: 'Alerta', label: 'Alerta', },
                            { value: 'Incidencia', label: 'Incidencia', },
                        ],
                        emptyValue: 0,
                    },
                    {
                        title: 'Módulo',
                        field: 'idModulo',
                        type: 'selectLista',
                        size: 3,
                        options: [{ value: 0, label: 'Todos' }, ...LISTAS.MODULO.getValues().map(tipo => ({ value: Number(tipo.id), label: tipo.nombre }))],
                        emptyValue: 0,
                    },
                    { title: 'Código', field: 'id', type: 'string', size: 2, searchOnEnter: true, },
                    {
                        title: 'Usuario/a',
                        field: 'idUsuario',
                        type: 'entity',
                        size: 4,
                        entidad: 'Usuario',
                        data: usuarioOptions,
                        columns: [{ Header: 'Nombre', accessor: 'nombre', width: '95%' }],
                        onOpen: () => resolveAllUsuarios(),
                    },
                    { title: 'Fecha desde', field: 'fechaDesde', type: 'date', size: 3 },
                    { title: 'Fecha hasta', field: 'fechaHasta', minValueField: 'fechaDesde', type: 'date', size: 3 },
                    { title: 'Mensaje', field: 'mensaje', type: 'string', size: 6, searchOnEnter: true, },
                    { title: 'Contenido', field: 'contenido', type: 'string', size: 12, searchOnEnter: true, },
                ]}
                onSearch={Search}
            />

            {tableVisible && (
                <TableCustom
                    className={'TableCustomBase m-top-20'}
                    columns={[
                        { Header: 'Código', accessor: 'id', width: '15%' },
                        { Header: 'Colección', accessor: 'tipo', width: '12%' },
                        { Header: 'Módulo', accessor: 'idModulo', Cell: LISTAS.MODULO.getCell(), width: '15%' },
                        { Header: 'Usuario/a', accessor: 'idUsuario', Cell: ({ value }) => getNombreUsuario(value), width: '10%' },
                        { Header: 'Fecha/Hora', accessor: 'fecha', Cell: ({ value }) => getDateToString(value, true), width: '20%' },
                        { Header: 'Mensaje', accessor: 'mensaje', },
                    ]}
                    data={items}
                    avmr={avmr}
                    messageEmpty="No se encontraron resultados"
                />
            )}
        </section>
    </>
}

export default HomeView;
